import React, { useState } from 'react';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import { auth, googleProvider } from './firebase';
import { signInWithPopup, fetchSignInMethodsForEmail, sendPasswordResetEmail } from 'firebase/auth';
import './Login.css';
import { AppLogo } from './design/AppLogo';
import { Icon } from './design/icons';

const fieldStyle = {
  width: '100%',
  height: 42,
  padding: '0 12px',
  background: 'var(--surface)',
  border: '1px solid var(--line)',
  borderRadius: 8,
  fontSize: 14,
  color: 'var(--ink)',
  outline: 'none',
};

// The app's one eyebrow, at the size the form needs it.
const labelStyle = {
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--ink-3)',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  marginBottom: 7,
  display: 'block',
};

function Login({ onLoginSuccess, authError }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleGoogle = async () => {
    setError('');
    setGoogleLoading(true);
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      if (err.code === 'auth/popup-blocked') {
        setError('Popup was blocked. Please allow popups for this site and try again.');
      } else if (err.code === 'auth/popup-closed-by-user') {
        // user dismissed — no error needed
      } else {
        setError(err.message);
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setError('');
    if (isSignUp && password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      if (isSignUp) {
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
      onLoginSuccess();
    } catch (err) {
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/account-exists-with-different-credential') {
        const methods = await fetchSignInMethodsForEmail(auth, email);
        if (methods.includes('google.com')) {
          setError('This email is registered with Google. Please use "Continue with Google" instead.');
        } else {
          setError('Incorrect email or password.');
        }
      } else if (err.code === 'auth/user-not-found') {
        setError('No account found with that email.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (!email) {
      setError('Enter your email above first.');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
      setError('');
    } catch (err) {
      if (err.code === 'auth/user-not-found') {
        setError('No account found with that email.');
      } else {
        setError(err.message);
      }
    }
  };

  return (
    <div className="login-page">
      <div className="login-shell">
      <div className="login-brand">
        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <AppLogo size={40} />
        </div>
        <div>
          <h1>Prisma</h1>
          <p>{isSignUp ? 'Create an account to start tracking what is safe to spend.' : 'Sign in to pick up where you left off.'}</p>
        </div>
      </div>

      <div>
        {authError && <div className="login-alert login-alert--error">{authError}</div>}

        <button type="button" className="login-google pressable" onClick={handleGoogle} disabled={googleLoading}>
          <svg width="18" height="18" viewBox="0 0 48 48">
            <path fill="#FFC107" d="M43.6 20H24v8h11.3C33.6 33.1 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.5 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 19.7-8 19.7-20 0-1.3-.1-2.7-.1-4z" />
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.5 29.3 4 24 4c-7.8 0-14.5 4.3-17.7 10.7z" />
            <path fill="#4CAF50" d="M24 44c5.2 0 9.9-1.9 13.5-5l-6.2-5.2C29.4 35.6 26.8 36 24 36c-5.2 0-9.6-3-11.3-7.3l-6.5 5C9.6 39.8 16.3 44 24 44z" />
            <path fill="#1976D2" d="M43.6 20H24v8h11.3c-.9 2.5-2.6 4.6-4.8 6l6.2 5.2C40.5 35.5 44 30.2 44 24c0-1.3-.1-2.7-.4-4z" />
          </svg>
          {googleLoading ? 'Signing in...' : 'Continue with Google'}
        </button>

        <div className="login-divider">
          <span>or</span>
        </div>

        <form onSubmit={handleAuth} className="login-form">
          <div>
            <label htmlFor="login-email" style={labelStyle}>Email</label>
            <input
              id="login-email"
              className="login-input"
              style={fieldStyle}
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div>
            <label htmlFor="login-password" style={labelStyle}>Password</label>
            <div style={{ position: 'relative' }}>
              <input
                id="login-password"
                className="login-input"
                style={{ ...fieldStyle, paddingRight: 42 }}
                type={showPassword ? 'text' : 'password'}
                autoComplete={isSignUp ? 'new-password' : 'current-password'}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="pressable"
                style={{ position: 'absolute', top: 0, right: 0, height: 42, width: 42, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-3)', background: 'transparent', border: 'none' }}
              >
                <Icon name={showPassword ? 'eyeOff' : 'eye'} size={18} />
              </button>
            </div>
          </div>

          {isSignUp && (
            <div>
              <label htmlFor="login-confirm-password" style={labelStyle}>Confirm password</label>
              <input
                id="login-confirm-password"
                className="login-input"
                style={fieldStyle}
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
          )}

          {!isSignUp && (
            <div className="login-forgot">
              <button type="button" className="login-link" onClick={handleReset}>
                Forgot password?
              </button>
            </div>
          )}

          {resetSent && <div className="login-alert login-alert--success">Reset link sent — check your email.</div>}
          {error && <div className="login-alert login-alert--error">{error}</div>}

          <button type="submit" className="login-submit pressable" disabled={loading}>
            {loading ? 'Signing in…' : isSignUp ? 'Create account' : 'Sign in'}
          </button>
        </form>

        <p className="login-toggle">
          {isSignUp ? 'Already have an account? ' : "Don't have an account? "}
          <button type="button" className="login-link" onClick={() => { setIsSignUp((s) => !s); setError(''); setConfirmPassword(''); }}>
            {isSignUp ? 'Sign in' : 'Sign up'}
          </button>
        </p>
      </div>

      <p className="login-footer">Your data is private and secure.</p>
      </div>
    </div>
  );
}

export default Login;
